import React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import { routes } from "../../routes/routes";
import ListIcon from "@mui/icons-material/List";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import MenuIcon from "@mui/icons-material/Menu";
import FolderIcon from "@mui/icons-material/Folder";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { useTheme } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import EventNoteIcon from "@mui/icons-material/EventNote";
import QuizIcon from "@mui/icons-material/Quiz";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useUserStore } from "../../store/useUserStore";
import SystemStatusIndicator from "../SystemStatusIndicator";
import { BANNER_COLOR, TEXT_COLOR } from "../../pages/root";
import logoAndes from "../../assets/LogoAndes.png";
import { useAuthStore } from "../../store/useAuthStore";
import { Link as RouterLink } from "react-router-dom";

const Navbar = ({ health, loading, error }) => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Sesión
  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);
  const isSignedIn = !!session?.authenticationToken;

  const user = useUserStore((state) => state.userInfo);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Elementos de navegación
  const navManagerItems = [
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role))
      ? [{ label: "Instrumentos", icon: <FolderIcon />, href: routes.instrumentos }]
      : []),
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role))
      ? [{ label: "Cuestionarios", icon: <EventNoteIcon />, href: routes.cuestionarios }]
      : []),
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role))
      ? [{ label: "Aspectos", icon: <DescriptionIcon />, href: routes.aspectos }]
      : []),
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role)) ? [{ label: "Preguntas", icon: <QuizIcon />, href: routes.preguntas }] : []),
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role))
      ? [{ label: "Tipos-opciones", icon: <ListIcon />, href: routes.tiposOpcionesRespuesta }]
      : []),
  ];

  const navAdminItems = [
    ...(user?.roles?.some((role) => ["manager", "admin"].includes(role)) ? [{ label: "Usuarios", icon: <PeopleIcon />, href: routes.usuarios }] : []),

    ...(user?.roles?.includes("admin")
      ? [
          {
            label: "Aplicaciones",
            icon: <AssignmentIcon />,
            href: routes.aplicaciones,
          },
          {
            label: "Reportes",
            icon: <AssessmentOutlinedIcon />,
            href: routes.reportes,
          },
        ]
      : []),
  ];

  const navUserItems = [{ label: "Inicio", icon: <HomeIcon />, href: routes.home }];

  const userMenu = (
    <>
      {navUserItems.map((item) => (
        <Button
          key={item.label}
          component={RouterLink}
          to={item.href}
          startIcon={item.icon}
          sx={{
            color: TEXT_COLOR,
            mx: 1,
            textTransform: "capitalize",
            borderRadius: 2,
            padding: "5px 12px",
            fontSize: ".9rem",
            "&:hover": { backgroundColor: "rgba(239, 227, 190, 0.8)" },
          }}
        >
          {item.label}
        </Button>
      ))}
    </>
  );

  const navItems = [...navUserItems, ...navManagerItems, ...navAdminItems];

  // Menú para pantallas pequeñas
  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.primary.main,
          color: "black",
          width: 250,
        },
      }}
    >
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.label}
            component={RouterLink}
            to={item.href}
            onClick={handleDrawerToggle}
            sx={{ "&:hover": { backgroundColor: "rgba(239, 227, 190, 0.8)" } }}
          >
            <ListItemIcon sx={{ color: "black" }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                sx: { textTransform: "capitalize", fontSize: "0.9rem", color: "black" },
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );

  // Menús de navegación (pantallas grandes)
  const DesktopMenu = ({ menuItems, label = "Menu", icon }) => {
    const [localAnchor, setLocalAnchor] = React.useState(null);
    const localOpen = Boolean(localAnchor);
    const handleLocalClick = (e) => setLocalAnchor(e.currentTarget);
    const handleLocalClose = () => setLocalAnchor(null);

    return (
      <>
        <Button
          aria-controls={localOpen ? `desktop-menu-${label}` : undefined}
          aria-haspopup="true"
          aria-expanded={localOpen ? "true" : undefined}
          startIcon={icon}
          onClick={handleLocalClick}
          sx={{ color: TEXT_COLOR, textTransform: "none", borderRadius: 2 }}
        >
          {label}
        </Button>
        <Menu
          id={`desktop-menu-${label}`}
          anchorEl={localAnchor}
          open={localOpen}
          onClose={handleLocalClose}
          MenuListProps={{ "aria-labelledby": undefined }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.label}
              component={RouterLink}
              to={item.href}
              onClick={handleLocalClose}
              sx={{ color: TEXT_COLOR, textTransform: "none" }}
            >
              <ListItemIcon sx={{ color: TEXT_COLOR }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ sx: { color: TEXT_COLOR } }} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  return (
    <Box>
      <AppBar position="fixed" sx={{ backgroundColor: BANNER_COLOR, color: TEXT_COLOR }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Button component={RouterLink} to={routes.home}>
              <img className="h-10" src={logoAndes} alt="Logo Universidad de Los Andes" />
            </Button>
          </Box>

          {/* Menú de navegación */}
          <Box sx={{ display: "grid", gridAutoFlow: "column", gap: 2, alignItems: "center", mr: 2 }}>
            {isSignedIn && userMenu}
            {isSignedIn && navManagerItems.length > 0 && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", mr: 2 }}>
                <DesktopMenu menuItems={navManagerItems} label="Gestión de cuestionarios" icon={<ManageSearchIcon />} />
              </Box>
            )}
            {isSignedIn && navAdminItems.length > 0 && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", mr: 2 }}>
                <DesktopMenu menuItems={navAdminItems} label="Administración" icon={<SupervisorAccountIcon />} />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isSignedIn && (
              <Button
                variant="text"
                onClick={logout}
                startIcon={<LogoutRoundedIcon />}
                sx={{
                  display: "inline-flex",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  color: TEXT_COLOR,
                  border: "none !important",
                  outline: "none",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "rgba(239, 227, 190, 0.8)" },
                }}
              >
                Salir
              </Button>
            )}

            {/* Indicador de healthcheck */}
            {isSignedIn && user?.roles?.some((role) => ["admin", "manager"].includes(role)) && (
              <SystemStatusIndicator health={health} error={error} loading={loading} />
            )}

            {/* Menú en pantallas más pequeñas */}
            {isSignedIn && (
              <IconButton
                color="inherit"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ display: { xs: "block", md: "none" } }}
                aria-label="Abrir menú"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menú Desplegable */}
      {isSignedIn && mobileMenu}
    </Box>
  );
};

export default Navbar;
